import axios from "axios";
import { createContext, useContext, useEffect, useMemo, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Overrides shape: { [slug]: { price?, title?, tag?, caption?, description?,
//   material?, care?, mrp?, stock?, featured?, sizes?, colours?, images?, is_deleted?, sort_order? } }
// categoryOverrides shape: { [slug]: { image? } }
const Ctx = createContext({
  overrides: {},
  categoryOverrides: {},
  customProducts: [],
  categories: [],
  ready: false,
  refresh: () => {},
});

export const PricesProvider = ({ children }) => {
  const [overrides, setOverrides] = useState({});
  const [categoryOverrides, setCategoryOverrides] = useState({});
  const [customProducts, setCustomProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [ready, setReady] = useState(false);

  const refresh = () => {
    Promise.all([
      axios.get(`${API}/product-overrides`).then((r) => r.data || {}).catch(() => ({})),
      axios.get(`${API}/category-overrides`).then((r) => r.data || {}).catch(() => ({})),
      axios.get(`${API}/custom-products`).then((r) => r.data || []).catch(() => []),
      axios.get(`${API}/categories`).then((r) => r.data || []).catch(() => []),
    ])
      .then(([p, co, cp, cats]) => {
        setOverrides(p);
        setCategoryOverrides(co);
        setCustomProducts(cp);
        setCategories(cats);
      })
      .finally(() => setReady(true));
  };

  useEffect(() => { refresh(); }, []);

  const value = useMemo(
    () => ({ overrides, categoryOverrides, customProducts, categories, ready, refresh }),
    [overrides, categoryOverrides, customProducts, categories, ready]
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

// Merge a base product with any server-side override (deep-ish, first level only)
export const applyOverride = (base, override) => {
  if (!override) return base;
  return { ...base, ...override };
};

// Resolve a stored image path — accepts full URLs or relative /api/files/... paths.
export const resolveImage = (u) => {
  if (!u) return u;
  if (u.startsWith("http://") || u.startsWith("https://") || u.startsWith("data:")) return u;
  return `${process.env.REACT_APP_BACKEND_URL}${u.startsWith("/") ? "" : "/"}${u}`;
};

export const usePrices = () => useContext(Ctx);

// Merge base PRODUCTS array (from anabiya.js) with overrides + custom products,
// removing anything soft-deleted, then sorted by sort_order.
export const useEffectiveProducts = (basePRODUCTS) => {
  const { overrides, customProducts } = usePrices();
  return useMemo(() => {
    const baseIdx = new Map();
    basePRODUCTS.forEach((p, i) => baseIdx.set(p.slug, (i + 1) * 10));
    const base = basePRODUCTS
      .map((p) => {
        const ov = overrides[p.slug];
        if (ov?.is_deleted) return null;
        const merged = applyOverride(p, ov);
        return { ...merged, kind: "base", sort_order: ov?.sort_order ?? baseIdx.get(p.slug) };
      })
      .filter(Boolean);
    const customs = (customProducts || [])
      .filter((c) => !c.is_deleted)
      .map((c) => ({
        ...c,
        kind: "custom",
        rating: c.rating ?? 0,
        reviewCount: c.reviewCount ?? 0,
        images: c.images && c.images.length ? c.images : [],
      }));
    return [...base, ...customs].sort(
      (a, b) => (a.sort_order ?? 999999) - (b.sort_order ?? 999999)
    );
  }, [basePRODUCTS, overrides, customProducts]);
};

// Merged categories = server list (respecting rename/delete/new). Falls back to
// the passed baseCATEGORIES if the server list has not loaded yet.
export const useEffectiveCategories = (baseCATEGORIES) => {
  const { categories, categoryOverrides } = usePrices();
  return useMemo(() => {
    if (categories && categories.length) {
      // Preserve original base image if server hasn't uploaded one.
      const baseImgMap = new Map((baseCATEGORIES || []).map((c) => [c.slug, c.image]));
      return categories.map((c) => ({
        slug: c.slug,
        label: c.label,
        sort_order: c.sort_order ?? 100,
        image:
          resolveImage(categoryOverrides?.[c.slug]?.image) ||
          resolveImage(c.image) ||
          baseImgMap.get(c.slug) ||
          null,
      }));
    }
    return (baseCATEGORIES || []).map((c) => ({
      ...c,
      image: resolveImage(categoryOverrides?.[c.slug]?.image) || c.image,
    }));
  }, [categories, categoryOverrides, baseCATEGORIES]);
};
